/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 27/05/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Refocus del lector tras acciones
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
*      4       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminaciones en submenú de otras acciones
*      5        | 09/07/2026 | Lic. José Luis Aragón Cervantes  | Ciclo NUEVO/GUARDAR/EDITAR/APLICAR + folio
 *      6        | 23/09/2026 | LIC. José Luis Aragón C.                       | Botón Historial (buscador por máquina)
 *      7        | 25/09/2026 | LIC. José Luis Aragón C.                       | Historial del día vía modal periodo SQL
 *      8        | 04/10/2026 | LIC. José Luis Aragón C.                       | Mecánica Salidas: TR bloquea todo
 *              |            |                                  | excepto Buscar/Salir; Aplicar exige
 *              |            |                                  | GUARDAR con autofinado previo
 * ---------------------------------------------------------------------------------------
 */

import { IconButton, Tooltip } from "@mui/material";
import RestorePageIcon from '@mui/icons-material/RestorePage';
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DirectionsIcon from "@mui/icons-material/Directions";
import VerifiedIcon from "@mui/icons-material/Verified";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from '@mui/icons-material/Print';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';

import { Fragment, useEffect, useCallback, useState } from "react";
import { useSesion } from "../../context/System/SesionContext";
import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { useDoubleClickPrevention } from "../../hooks/useDoubleClickPrevention";
import MnuDangerActionsMenu from "../utils/MnuDangerActionsMenu";
import ModalReporteTrasladosDia from "./ModalReporteTrasladosDia.jsx";

export default function MnuTrasladosAlmacenesExhibidoresActions(props) {
    const { onOpenBuscador, onAfterAction, onRefresh, onDeletePartida, onDeleteTraslado, onNuevoTraslado, onGuardarTraslado, onAplicarTraslado, onMostrarVistaPrevia, onBuscarTraslados, onSalir, dataLength, isBlocked, isEditionLocked = false, titleActionSave, movimientoBloqueado: movimientoBloqueadoProp, statusTraslado } = props;
    const {
        handleClickRegresarMainDashboard,
        errorDestino,
        handleCargarTrasladosPC,
        handleClickDeletePartida,
        handleClickDeleteTraslado,
        handleClickNuevoTraslado,
        partidaTrasladoSeleccionada,
        listaTrasladosData,
        handleAplicarTrasladoPC,
        handleImprimirTraslado,
        handleAbrirModalBuscarArticulos,
        handleAbrirFirmaTraslados,
        handleAbrirValidarAccesoTraslados,
        isTrasladoBlocked,
    } = useInventarios();

    const { parametrosCajaForm } = useSesion();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();
    // Rev 7: el historial del día ya no usa el GET obsoleto (404); abre el
    // modal por periodo que genera el PDF desde SQL/temporales finados.
    const [openHistorialDia, setOpenHistorialDia] = useState(false);

    const hasData = dataLength !== undefined ? dataLength > 0 : listaTrasladosData.length > 0;
    const blocked = isBlocked !== undefined ? isBlocked : isTrasladoBlocked;
    // Rev 8: mecánica Salidas — el status TR/CA manda. En TR nada es editable
    // excepto Buscar traslados / Salir (e Historial de solo lectura).
    const statusCabecera = String(
        statusTraslado
        ?? listaTrasladosData?.[0]?.statusentradasalida
        ?? listaTrasladosData?.[0]?.statustraslado
        ?? listaTrasladosData?.[0]?.status
        ?? ""
    ).trim().toUpperCase();
    const movimientoBloqueado = !!movimientoBloqueadoProp
        || statusCabecera === "TR"
        || statusCabecera === "CA"
        || blocked;
    const lockEdicion = movimientoBloqueado || isEditionLocked;
    // En TR el botón muestra EDITAR pero deshabilitado (igual que Salidas).
    const textoBoton = movimientoBloqueado ? "EDITAR" : (titleActionSave || "NUEVA");
    // El botón Guardar/Editar sigue vivo en EDITAR (es la vía para
    // desbloquear a GUARDAR), igual que Salidas; solo TR lo apaga.
    const puedeEditarGuardar = hasData && !movimientoBloqueado;
    // Aplicar exige estar en GUARDAR (con finado previo en el wrapper),
    // igual que Salidas: GUARDAR = pendiente de finar en SQL.
    const puedeAplicar = hasData && !movimientoBloqueado && !isEditionLocked && titleActionSave === "GUARDAR";
    const puedeVistaPrevia = hasData && !movimientoBloqueado;

    const refocusAfterAction = () => {
        if (typeof onAfterAction === "function") {
            onAfterAction();
        }
    };

    const handleRefresh = async () => {
        if (typeof onRefresh === 'function') {
            await onRefresh();
        } else {
            await handleCargarTrasladosPC();
        }
        refocusAfterAction();
    };

    const handleEliminaPartidaTrasladoCaja = async () => {
        if (!partidaTrasladoSeleccionada) return;
        if (typeof onDeletePartida === 'function') {
            await onDeletePartida(partidaTrasladoSeleccionada);
        } else {
            await handleClickDeletePartida(null, partidaTrasladoSeleccionada);
        }
        refocusAfterAction();
    };

    const handleEliminaTraslado = async () => {
        if (typeof onDeleteTraslado === 'function') {
            await onDeleteTraslado();
        } else {
            await handleClickDeleteTraslado(parametrosCajaForm.caja.trim());
        }
        refocusAfterAction();
    };

    const handleNuevoTraslado = async () => {
        if (typeof onNuevoTraslado === 'function') {
            await onNuevoTraslado();
        } else {
            await handleClickNuevoTraslado(parametrosCajaForm.caja.trim());
        }
        refocusAfterAction();
    };

    const handleGuardarTraslado = async () => {
        if (typeof onGuardarTraslado === 'function') {
            await onGuardarTraslado();
        }
        refocusAfterAction();
    };

    const handleAplicarTrasladoCaja = async () => {
        if (!listaTrasladosData) return;
        if (typeof onAplicarTraslado === 'function') {
            await onAplicarTraslado();
        } else {
            await handleAplicarTrasladoPC(null, parametrosCajaForm.caja.trim());
        }
        refocusAfterAction();
    };

    const handleImprimirTrasladoCaja = async () => {
        if (typeof onMostrarVistaPrevia === 'function') {
            await onMostrarVistaPrevia();
        } else {
            if (!listaTrasladosData) return;
            await handleImprimirTraslado(parametrosCajaForm.caja.trim());
        }
        refocusAfterAction();
    };

    const handleImprimirHistorialDia = async () => {
        setOpenHistorialDia(true);
        refocusAfterAction();
    };

    const handleKeyDown = useCallback(async (event) => {
        switch (event.key) {
            case 'F1':
                event.preventDefault();
                if (lockEdicion) break;
                await handleAbrirModalBuscarArticulos();
                break;
            default:
                break;
        }
    }, [partidaTrasladoSeleccionada, parametrosCajaForm, lockEdicion]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    return (
        <Fragment>
            <Tooltip title={"Home"}>
                <IconButton onClick={() => executeWithProtection(onSalir || handleClickRegresarMainDashboard)} disabled={isProcessing}>
                    <HomeIcon className="icon" /><p className="icon">Salir</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={"Nuevo"}>
                <IconButton onClick={() => executeWithProtection(handleNuevoTraslado)} disabled={isProcessing}>
                    <AddIcon className="icon" /><p className="icon">Nuevo</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={textoBoton === "GUARDAR" ? "Guardar" : "Editar"}>
                <span>
                    <IconButton onClick={() => executeWithProtection(handleGuardarTraslado)} disabled={isProcessing || !puedeEditarGuardar}>
                        {textoBoton === "GUARDAR" ? <SaveIcon className="icon" /> : <EditIcon className="icon" />}
                        <p className="icon">{textoBoton === "NUEVA" ? "Editar" : textoBoton}</p>
                    </IconButton>
                </span>
            </Tooltip>
            {!errorDestino && (
                <Tooltip title={"Aplicar"} disabled={!puedeAplicar}>
                    <span>
                        <IconButton onClick={() => executeWithProtection(handleAbrirValidarAccesoTraslados)} disabled={isProcessing || !puedeAplicar}>
                            <SaveAltIcon className="icon" /><p className="icon">Aplicar</p>
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            <Tooltip title={"Vista previa"} disabled={!puedeVistaPrevia}>
                <IconButton onClick={() => executeWithProtection(handleImprimirTrasladoCaja)} disabled={isProcessing || !puedeVistaPrevia}>
                    <PrintIcon className="icon" /><p className="icon">Vista previa antes de aplicar</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={"Imprimir historial del día"}>
                <IconButton onClick={() => executeWithProtection(handleImprimirHistorialDia)} disabled={isProcessing}>
                    <HistoryIcon className="icon" /><p className="icon">Historial del día</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={"Buscar traslados de la máquina"}>
                <IconButton onClick={() => { if (typeof onBuscarTraslados === "function") executeWithProtection(onBuscarTraslados); refocusAfterAction(); }} disabled={isProcessing}>
                    <SearchIcon className="icon" /><p className="icon">Traslados</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={"Artículos (Alt+F1)"}>
                <IconButton onClick={() => executeWithProtection(async () => {
                    if (onOpenBuscador) onOpenBuscador();
                    await handleAbrirModalBuscarArticulos();
                })} disabled={isProcessing || lockEdicion}>
                    <SearchIcon className="icon" /><p className="icon">Artículos</p>
                </IconButton>
            </Tooltip>
            <Tooltip title="Refrescar">
                <IconButton onClick={() => executeWithProtection(handleRefresh)} disabled={isProcessing}>
                    <RefreshIcon className="icon" />
                </IconButton>
            </Tooltip>
            <MnuDangerActionsMenu
                tooltip="Otras acciones"
                actions={[
                    {
                        label: "Eliminar partida",
                        onClick: handleEliminaPartidaTrasladoCaja,
                        disabled: movimientoBloqueado || lockEdicion || !hasData,
                    },
                    {
                        label: "Eliminar traslado",
                        onClick: handleEliminaTraslado,
                        disabled: movimientoBloqueado || lockEdicion || !hasData,
                    },
                ]}
            />
            <ModalReporteTrasladosDia
                openModal={openHistorialDia}
                onClose={() => {
                    setOpenHistorialDia(false);
                    refocusAfterAction();
                }}
            />
        </Fragment>
    );
}
